import { getEventDetails } from '@store/slices/eventSlice';
import {
  ColoredWrapper,
  DateColumn,
  DateDay,
  DateMonth,
  DateYear,
  DescriptionText,
  DesktopFooterWrapper,
  FooterItemWrapper,
  InfoColumn,
  LocationWrapper,
  MoreDetailsWrapper,
  SessionInfoRoot,
  TitleText,
  Underline,
} from './styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SessionItem } from '..';
import { useMobileWidth, useTabletWidth } from '@utils/hooks';
import Icon from '@components/Icon';
import { useTheme } from 'styled-components';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { getSessionTimeWithoutTimezone } from '@utils/time';
import { DEFAULT_TIMEZONE } from '@utils/constants';

const SessionInfoItem = ({ item }: { item: SessionItem }) => {
  const {
    start_date: startDate,
    end_date: endDate,
    title,
    sub_title: subTitle,
    description,
    location_name: locationName,
    city,
  } = item;
  const { t } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const isTablet = useTabletWidth();
  const eventTimezone = sluggedEvent?.timezone ?? DEFAULT_TIMEZONE;

  const { day, month, year, time } = useMemo(() => {
    const zonedStart = dayjs.utc(startDate).tz(eventTimezone);
    const zonedEnd = dayjs.utc(endDate).tz(eventTimezone);

    const convertedStart = zonedStart.format('YYYY-MM-DDTHH:mm:ss.SSSSSSZ');
    const convertedEnd = zonedEnd.format('YYYY-MM-DDTHH:mm:ss.SSSSSSZ');

    return {
      day: zonedStart.format('DD'),
      month: zonedStart.format('MMM').toUpperCase(),
      year: zonedStart.format('YYYY'),
      time: getSessionTimeWithoutTimezone(convertedStart, convertedEnd),
    };
  }, [startDate, endDate, eventTimezone]);

  const [isAllVisible, setIsAllVisible] = useState<boolean>(false);

  const toggleMoreLess = () => {
    setIsAllVisible(!isAllVisible);
  };

  return (
    <SessionInfoRoot>
      <ColoredWrapper backgroundColor={sluggedEvent?.design?.themeColor}>
        <DateColumn>
          <DateDay>{day}</DateDay>
          <DateMonth>{month}</DateMonth>
          <DateYear>{year}</DateYear>
        </DateColumn>

        {(isMobile || isTablet) && (
          <div>
            <FooterItemWrapper style={{ justifyContent: 'end' }}>
              {time}
              <Icon color={theme.color.white} name="clock" size={24} />
            </FooterItemWrapper>

            <FooterItemWrapper
              style={{ justifyContent: 'end', fontSize: theme.fontSize.size14 }}
            >
              {t('live-at')}
              <LocationWrapper>
                {locationName} {city && city}{' '}
              </LocationWrapper>
              <Icon color={theme.color.white} name="location" size={24} />
            </FooterItemWrapper>
          </div>
        )}
      </ColoredWrapper>

      <InfoColumn>
        <div>
          <TitleText>{title}</TitleText>
          {subTitle && <div>{subTitle}</div>}
          <Underline color={sluggedEvent?.design?.themeColor} />
        </div>

        <DescriptionText
          isExpanded={isAllVisible}
          dangerouslySetInnerHTML={{ __html: description || '' }}
        />

        {(isMobile || isTablet) && (
          <MoreDetailsWrapper onClick={toggleMoreLess} justifyContent="start">
            <Icon
              color={theme.color.shadeGrey2}
              name={`event-pages_${isAllVisible ? 'up' : 'down'}-arrow`}
              size={24}
            />
            {t(isAllVisible ? 'less-details' : 'more-details')}
          </MoreDetailsWrapper>
        )}

        {!isMobile && !isTablet && (
          <DesktopFooterWrapper>
            <FooterItemWrapper>
              <Icon
                color={sluggedEvent?.design?.themeColor}
                name="clock"
                size={24}
              />
              {time}
            </FooterItemWrapper>

            <FooterItemWrapper>
              <Icon
                color={sluggedEvent?.design?.themeColor}
                name="location"
                size={24}
              />
              {t('live-at')}
              <LocationWrapper>
                {locationName} {city && city}{' '}
              </LocationWrapper>
            </FooterItemWrapper>

            <MoreDetailsWrapper onClick={toggleMoreLess}>
              <Icon
                color={theme.color.shadeGrey2}
                name={`event-pages_${isAllVisible ? 'up' : 'down'}-arrow`}
                size={24}
              />
              {t(isAllVisible ? 'less-details' : 'more-details')}
            </MoreDetailsWrapper>
          </DesktopFooterWrapper>
        )}
      </InfoColumn>
    </SessionInfoRoot>
  );
};

SessionInfoItem.displayName = 'SessionInfoItem';

export default SessionInfoItem;
